import React, { useState } from "react";
import './apiDebug.css';

export const JsonViewer = ({ data, title, defaultExpanded = false }) => {
  const [expandedPaths, setExpandedPaths] = useState(new Set(defaultExpanded ? ['root'] : []));
  const [copiedPath, setCopiedPath] = useState(null);

  const togglePath = (path) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const isExpanded = (path) => expandedPaths.has(path);

  const copyToClipboard = (text, path) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    }).catch(() => {});
  };

  const renderValue = (value, path, key = null, level = 0) => {
    const currentPath = key ? `${path}.${key}` : path;

    if (value === null) {
      return <span className="json-null">null</span>;
    }

    if (value === undefined) {
      return <span className="json-undefined">undefined</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="json-boolean">{String(value)}</span>;
    }

    if (typeof value === 'number') {
      return <span className="json-number">{value}</span>;
    }

    if (typeof value === 'string') {
      const isLongString = value.length > 100;
      const displayValue = isLongString ? value.substring(0, 100) + '...' : value;
      return (
        <span>
          <span className="json-string">"{displayValue}"</span>
          {isLongString && (
            <span className="json-string-info" title={value}>
              (truncado, {value.length} caracteres)
            </span>
          )}
        </span>
      );
    }

    if (Array.isArray(value)) {
      const isExp = isExpanded(currentPath);
      const isEmpty = value.length === 0;

      return (
        <div className="json-array">
          <span
            className="json-toggle"
            onClick={() => !isEmpty && togglePath(currentPath)}
            style={{ cursor: isEmpty ? 'default' : 'pointer' }}
          >
            {!isEmpty && (isExp ? '▼' : '▶')}
            <span className="json-bracket">[</span>
          </span>
          {isEmpty ? (
            <span className="json-empty">vacío</span>
          ) : (
            <>
              {!isExp && <span className="json-count"> {value.length} elemento{value.length !== 1 ? 's' : ''}</span>}
              {isExp && (
                <div className="json-children">
                  {value.map((item, index) => (
                    <div key={`${currentPath}-${index}`} className="json-item">
                      <span className="json-key">{index}:</span>
                      {renderValue(item, currentPath, index, level + 1)}
                      {index < value.length - 1 && <span className="json-comma">,</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <span className="json-bracket">]</span>
        </div>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      const isExp = isExpanded(currentPath);
      const isEmpty = keys.length === 0;

      return (
        <div className="json-object">
          <span
            className="json-toggle"
            onClick={() => !isEmpty && togglePath(currentPath)}
            style={{ cursor: isEmpty ? 'default' : 'pointer' }}
          >
            {!isEmpty && (isExp ? '▼' : '▶')}
            <span className="json-bracket">{'{'}</span>
          </span>
          {isEmpty ? (
            <span className="json-empty">vacío</span>
          ) : (
            <>
              {!isExp && <span className="json-count"> {keys.length} propiedad{keys.length !== 1 ? 'es' : ''}</span>}
              {isExp && (
                <div className="json-children">
                  {keys.map((k, index) => (
                    <div key={`${currentPath}-${k}`} className="json-item">
                      <span className="json-key">"{k}"</span>
                      <span className="json-colon">:</span>
                      {renderValue(value[k], currentPath, k, level + 1)}
                      {index < keys.length - 1 && <span className="json-comma">,</span>}
                      <button
                        className="json-copy-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(JSON.stringify(value[k], null, 2), `${currentPath}.${k}`);
                        }}
                        title="Copiar valor"
                      >
                        {copiedPath === `${currentPath}.${k}` ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <span className="json-bracket">{'}'}</span>
        </div>
      );
    }

    return <span className="json-unknown">{String(value)}</span>;
  };

  const renderJson = () => {
    try {
      if (data === null || data === undefined) {
        return <span className="json-null">null</span>;
      }
      return renderValue(data, 'root', null, 0);
    } catch (e) {
      return <span className="json-error">Error al renderizar: {e.message}</span>;
    }
  };

  const expandAll = () => {
    if (data === null || data === undefined) return;
    const allPaths = new Set();
    const collectPaths = (obj, path = 'root') => {
      allPaths.add(path);
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            collectPaths(item, `${path}.${index}`);
          }
        });
      } else if (typeof obj === 'object' && obj !== null) {
        Object.keys(obj).forEach(key => {
          const val = obj[key];
          if (typeof val === 'object' && val !== null) {
            collectPaths(val, `${path}.${key}`);
          }
        });
      }
    };
    collectPaths(data);
    setExpandedPaths(allPaths);
  };

  const collapseAll = () => {
    setExpandedPaths(new Set());
  };

  return (
    <div className="json-viewer-container">
      {title && <div className="json-viewer-title">{title}</div>}
      <div className="json-viewer-controls">
        <button className="json-control-btn" onClick={expandAll} title="Expandir todo">
          ▼ Expandir todo
        </button>
        <button className="json-control-btn" onClick={collapseAll} title="Colapsar todo">
          ▶ Colapsar todo
        </button>
        <button
          className="json-control-btn"
          onClick={() => copyToClipboard(JSON.stringify(data, null, 2), 'all')}
          title="Copiar JSON completo"
        >
          {copiedPath === 'all' ? '✓ Copiado' : '📋 Copiar JSON'}
        </button>
      </div>
      <div className="json-viewer-content-interactive">
        {renderJson()}
      </div>
    </div>
  );
};

export const CollapsibleSection = ({
  title,
  isExpanded,
  onToggle,
  children,
  icon = null,
  badge = null,
  headerColor = '#e2e2e2cc'
}) => {
  return (
    <div className="collapsible-section">
      <div
        className="collapsible-header"
        onClick={onToggle}
        style={{ backgroundColor: headerColor }}
      >
        <div className="collapsible-header-content">
          {icon && <span className="section-icon">{icon}</span>}
          <h3 className="collapsible-title">{title}</h3>
          {badge && <span className="section-badge">{badge}</span>}
        </div>
        <span className="collapse-icon">
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      {isExpanded && (
        <div className="collapsible-content">
          {children}
        </div>
      )}
    </div>
  );
};
